import momentTz from 'moment-timezone';

import { CT_TIMEZONE_AREA } from '../../../constants';
import {
  BAY_EXEMPT_FROM_CLAIMS,
  RELAX_CLAIM_VALIDATIONS_PASSWORD,
  SessionClaimStatus,
} from '../../../components/layout/billings/claims/constants';

import type { FacilityAssignmentType } from '../../../constants';
import type {
  IClientAssignment,
  IClientFacilityMap,
  IClientPolicyMap,
  IProviderLicensesMapping,
  IProviderPayorCredentialsMapping,
  IPriorAuthorizationDetail,
  ISessionWithClaimStatus,
} from '../../../utils/types';

/** True when the user opted in and entered the correct relax-validations password. */
export function isRelaxClaimValidationsActive(
  relaxClaimValidationsChecked: boolean,
  relaxClaimValidationsPassword: string,
): boolean {
  return (
    relaxClaimValidationsChecked &&
    relaxClaimValidationsPassword === RELAX_CLAIM_VALIDATIONS_PASSWORD
  );
}

/**
 * Calculates units left from prior authorization: approved_units - units_consumed - numberOfUnitsSelected.
 * Returns undefined if approved_units is null/undefined (but not 0).
 */
export function calculateUnitsLeft(
  priorAuthDetails: IPriorAuthorizationDetail | null | undefined,
  numberOfUnitsSelected: number | undefined,
): number | undefined {
  const approved = priorAuthDetails?.approved_units;
  if (approved === undefined || approved === null) return undefined;
  return approved - (priorAuthDetails?.units_consumed ?? 0) - (numberOfUnitsSelected ?? 0);
}

/**
 * Filters policies to those that are PRIMARY payor and overlap with the calendar date range.
 * For monthly plans, extends date_end by one month when determining overlap.
 */
export function filterActivePrimaryPoliciesForDateRange(
  policies: IClientPolicyMap[],
  calendarDate: { startDate: Date; endDate: Date },
): IClientPolicyMap[] {
  return policies.filter((policy) => {
    const policyEndDate = !policy.date_end
      ? null
      : policy.is_monthly_plan
        ? momentTz(policy.date_end).add(1, 'month').tz(CT_TIMEZONE_AREA).endOf('day')
        : momentTz(policy.date_end).tz(CT_TIMEZONE_AREA).endOf('day');

    return (
      policy.payor_responsibility === 'PRIMARY' &&
      momentTz(policy.date_start)
        .tz(CT_TIMEZONE_AREA)
        .startOf('day')
        .isSameOrBefore(momentTz(calendarDate.endDate).tz(CT_TIMEZONE_AREA).startOf('day')) &&
      (!policyEndDate ||
        momentTz(policyEndDate)
          .tz(CT_TIMEZONE_AREA)
          .endOf('day')
          .isAfter(momentTz(calendarDate.startDate).tz(CT_TIMEZONE_AREA).endOf('day')))
    );
  });
}

/**
 * Filters cohort assignments to those that overlap with the calendar date range.
 * A cohort overlaps if it started on or before the range end and (has no end date or ended after the range start).
 */
export function filterCohortAssignmentsForDateRange(
  cohorts: IClientAssignment[],
  calendarDate: { startDate: Date; endDate: Date },
): IClientAssignment[] {
  return cohorts.filter((cohort) => {
    const start = momentTz(cohort.date_start)
      .tz(CT_TIMEZONE_AREA)
      .startOf('day')
      .isSameOrBefore(momentTz(calendarDate.endDate).tz(CT_TIMEZONE_AREA).startOf('day'));
    const end =
      !cohort.date_end ||
      momentTz(cohort.date_end)
        .tz(CT_TIMEZONE_AREA)
        .endOf('day')
        .isAfter(momentTz(calendarDate.startDate).tz(CT_TIMEZONE_AREA).startOf('day'));
    return start && end;
  });
}

/** Shape of a single group when sessions are grouped by bay, clinical relationship, month, and provider. */
export type GroupedSessionEntry = {
  sessions: ISessionWithClaimStatus[];
  bay: string | null;
  clinicalRelationship: string | null;
  monthKey: string;
  providerId: number | null | undefined;
  authorDisplayName: string | null;
};

type GroupedSessionsResult = {
  grouped: Record<string, GroupedSessionEntry>;
  sortedKeys: string[];
};

type ProviderIdsForLicensingAndPayorCredentials = {
  providerIdsForLicensing: string[];
  providerIdsForPayorCredentials: string[];
};

/**
 * Groups sessions by bay, clinical relationship, month, and provider.
 * Returns the grouped record and sorted keys (by bay numeric, then clinical relationship, month, provider).
 */
export function groupSessionsByBayClinicalRelationshipAndMonth(
  sessions: ISessionWithClaimStatus[],
): GroupedSessionsResult {
  const grouped = sessions.reduce<Record<string, GroupedSessionEntry>>((acc, session) => {
    const bayKey = session.bay ?? null;
    const clinicalRelationshipKey = session.clinical_relationship ?? null;
    const monthKey = momentTz(session.date).tz(CT_TIMEZONE_AREA).format('YYYY-MM');
    const providerId = session.provider_id ?? null;
    const authorDisplayName = session.provider_name ?? null;
    const compositeKey = `${bayKey}__${clinicalRelationshipKey}__${monthKey}__${providerId}`;

    if (!acc[compositeKey]) {
      acc[compositeKey] = {
        sessions: [],
        bay: bayKey,
        clinicalRelationship: clinicalRelationshipKey,
        monthKey,
        providerId,
        authorDisplayName,
      };
    }
    acc[compositeKey].sessions.push(session);
    return acc;
  }, {});

  const sortedKeys = Object.keys(grouped).sort((a, b) => {
    const partsA = a.split('__');
    const partsB = b.split('__');
    const [bayA, crA, monthA, providerA] = partsA;
    const [bayB, crB, monthB, providerB] = partsB;

    if (bayA !== bayB) {
      if (!bayA) return 1;
      if (!bayB) return -1;
      const numA = parseFloat(bayA);
      const numB = parseFloat(bayB);
      return (isNaN(numA) ? 0 : numA) - (isNaN(numB) ? 0 : numB);
    }
    if (crA !== crB) return crA.localeCompare(crB);
    if (monthA !== monthB) return monthA.localeCompare(monthB);
    const numProviderA = providerA ? parseFloat(providerA) : NaN;
    const numProviderB = providerB ? parseFloat(providerB) : NaN;
    return (isNaN(numProviderA) ? 0 : numProviderA) - (isNaN(numProviderB) ? 0 : numProviderB);
  });

  return { grouped, sortedKeys };
}

/**
 * Returns provider IDs that need to be fetched for licensing and payor credentials.
 * Compares sessions' author IDs against already-fetched mappings to find missing providers.
 */
export function getProviderIdsForLicensingAndPayorCredentials(
  providerLicensesMapping: IProviderLicensesMapping,
  providerPayorCredentialsMapping: IProviderPayorCredentialsMapping,
  filteredSessions: Array<{ author_id?: number }>,
  selectedBillingPayor: { state: string; id?: number },
): ProviderIdsForLicensingAndPayorCredentials {
  const fetchedLicenseProviderIds = Object.keys(providerLicensesMapping);
  const fetchedPayorCredentialsProviderIds = Object.keys(providerPayorCredentialsMapping);

  const uniqueAuthorIds = Array.from(
    new Set(
      filteredSessions.flatMap((session) =>
        session.author_id === undefined ? [] : [session.author_id],
      ),
    ),
  );
  const licensingKeys = uniqueAuthorIds.map((id) => `${id}-${selectedBillingPayor.state}`);
  const payorCredentialsKeys = uniqueAuthorIds.map((id) => `${id}-${selectedBillingPayor.id}`);

  const providerIdsForLicensing = licensingKeys.filter(
    (key) => !fetchedLicenseProviderIds.includes(key),
  );
  const providerIdsForPayorCredentials = payorCredentialsKeys.filter(
    (key) => !fetchedPayorCredentialsProviderIds.includes(key),
  );

  return { providerIdsForLicensing, providerIdsForPayorCredentials };
}

/**
 * Finds a facility of the given assignment type (BILLING or SERVICE) that is active
 * for the calendar date range. Facility must have started on or before the range start
 * and (have no end date or end after the range end).
 */
export const findFacilityForDateRange = (
  facilities: IClientFacilityMap[],
  assignmentType: FacilityAssignmentType,
  calendarDate: { startDate: Date; endDate: Date },
): IClientFacilityMap | undefined =>
  facilities.find(
    (facility) =>
      facility.assignment_type === assignmentType &&
      momentTz(facility.date_start)
        .tz(CT_TIMEZONE_AREA)
        .startOf('day')
        .isSameOrBefore(momentTz.utc(calendarDate.startDate).tz(CT_TIMEZONE_AREA).startOf('day')) &&
      (!facility.date_end ||
        momentTz(facility.date_end)
          .tz(CT_TIMEZONE_AREA)
          .endOf('day')
          .isAfter(momentTz.utc(calendarDate.endDate).tz(CT_TIMEZONE_AREA).endOf('day'))),
  );

export const getPolicyLabel = (policy: IClientPolicyMap): string =>
  `${policy.member_id || policy.id} (${policy.payor.label})`;

export const filterExemptBaySessions = (
  sessions: ISessionWithClaimStatus[],
): ISessionWithClaimStatus[] =>
  sessions.filter(
    (session) =>
      !BAY_EXEMPT_FROM_CLAIMS.some(
        (bay) => bay === (session.bay?.trim().toUpperCase() ?? ''),
      ),
  );

export const getClaimedSessions = (
  sessions: ISessionWithClaimStatus[],
): ISessionWithClaimStatus[] =>
  sessions.filter(
    (session) =>
      session.status !== SessionClaimStatus.Unclaimed &&
      session.status !== SessionClaimStatus.NotReady,
  );

export const getUnclaimedSessions = (
  sessions: ISessionWithClaimStatus[],
): ISessionWithClaimStatus[] =>
  sessions.filter((session) => session.status === SessionClaimStatus.Unclaimed);

export const getNotReadySessions = (
  sessions: ISessionWithClaimStatus[],
): ISessionWithClaimStatus[] =>
  sessions.filter((session) => session.status === SessionClaimStatus.NotReady);

export const filterSessionsByDateRangeAndBay = (
  sessions: ISessionWithClaimStatus[],
  calendarDate: { startDate: Date; endDate: Date },
): ISessionWithClaimStatus[] => {
  const calendarStart = momentTz(calendarDate.startDate).tz(CT_TIMEZONE_AREA);
  const calendarEnd = momentTz(calendarDate.endDate).tz(CT_TIMEZONE_AREA);

  const filteredSessions = sessions.filter((session) => {
    const sessionDate = momentTz(session.date).tz(CT_TIMEZONE_AREA);
    return sessionDate.isSameOrAfter(calendarStart) && +sessionDate.isBefore(calendarEnd);
  });

  return filterExemptBaySessions(filteredSessions);
};
